import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  BadgeCheck,
  BookOpen,
  CheckCircle2,
  Clock3,
  FileText,
  Loader2,
  RefreshCw,
  Save,
  Search,
  ShieldCheck,
  Users,
  ChevronRight,
  AlertCircle,
  Layers3,
  CornerDownRight
} from 'lucide-react';
import api from '../api';

const statusClasses = {
  pending: 'bg-amber-50 text-amber-700 border-amber-100',
  in_progress: 'bg-blue-50 text-blue-700 border-blue-100',
  completed: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  reassigned: 'bg-violet-50 text-violet-700 border-violet-100'
};

const formatPercent = (value) => `${Math.round(Number(value || 0))}%`;

const flattenQuestions = (sections = []) => {
  const questions = [];
  sections.forEach((section) => {
    (section.questions || []).forEach((question) => {
      questions.push({
        key: String(question._id),
        questionId: String(question._id),
        sectionName: section.sectionName,
        questionNumber: question.questionNumber,
        questionText: question.questionText || '',
        maxMarks: Number(question.marks || 0),
        coMappings: question.coMappings || [],
        poMappings: question.poMappings || [],
        bloomLevel: question.bloomLevel || '',
        hasSubQuestions: !!question.hasSubQuestions,
        subQuestions: (question.subQuestions || []).map((subQuestion) => ({
          key: `${question._id}:${subQuestion._id}`,
          questionId: `${question._id}:${subQuestion._id}`,
          sectionName: section.sectionName,
          parentQuestionNumber: question.questionNumber,
          questionNumber: subQuestion.subQuestionNumber,
          questionText: subQuestion.questionText || '',
          maxMarks: Number(subQuestion.marks || 0),
          coMappings: subQuestion.coMappings || [],
          poMappings: subQuestion.poMappings || [],
          bloomLevel: subQuestion.bloomLevel || ''
        }))
      });
    });
  });
  return questions;
};

const createDraftFromStudent = (sections, student) => {
  const draft = {};
  const flatQuestions = flattenQuestions(sections);

  flatQuestions.forEach((question) => {
    const savedQuestion = student?.questionWiseMarks?.find((entry) => String(entry.questionId) === String(question.questionId));
    draft[question.questionId] = {
      marksObtained: savedQuestion?.obtainedMarks ?? savedQuestion?.marksObtained ?? student?.marksData?.[question.questionId]?.marksObtained ?? 0,
      maxMarks: question.maxMarks,
      questionText: question.questionText,
      questionNumber: question.questionNumber,
      sectionName: question.sectionName,
      coMappings: question.coMappings,
      poMappings: question.poMappings,
      bloomLevel: question.bloomLevel
    };

    (question.subQuestions || []).forEach((subQuestion) => {
      const savedSubQuestion = student?.questionWiseMarks?.find((entry) => String(entry.questionId) === String(subQuestion.questionId));
      draft[subQuestion.questionId] = {
        marksObtained: savedSubQuestion?.obtainedMarks ?? savedSubQuestion?.marksObtained ?? 0,
        maxMarks: subQuestion.maxMarks,
        questionText: subQuestion.questionText,
        questionNumber: subQuestion.questionNumber,
        sectionName: subQuestion.sectionName,
        parentQuestionNumber: subQuestion.parentQuestionNumber,
        coMappings: subQuestion.coMappings,
        poMappings: subQuestion.poMappings,
        bloomLevel: subQuestion.bloomLevel
      };
    });
  });

  return draft;
};

const calculateDraftTotal = (draft = {}) => {
  return Object.values(draft).reduce((sum, entry) => sum + Number(entry?.marksObtained || 0), 0);
};

const getStudentName = (student) => {
  const name = student?.studentName || `${student?.firstName || ''} ${student?.lastName || ''}`.trim();
  return name || 'Unknown Student';
};

const getStudentUsn = (student) => {
  return student?.USN || student?.studentUSN || student?.rollNumber || student?.rollNo || 'No ID';
};

const getStudentStatusMeta = (student, sections = []) => {
  const requiredQuestions = flattenQuestions(sections);
  const requiredIds = [];

  requiredQuestions.forEach((question) => {
    requiredIds.push(String(question.questionId));
    (question.subQuestions || []).forEach((subQuestion) => {
      requiredIds.push(String(subQuestion.questionId));
    });
  });

  const savedQuestionIds = new Set((student?.questionWiseMarks || []).map((entry) => String(entry.questionId)));
  const savedMarksDataIds = new Set(
    student?.marksData && typeof student.marksData === 'object'
      ? Object.keys(student.marksData).map((key) => String(key))
      : []
  );

  const completedCount = requiredIds.filter((questionId) => {
    return savedQuestionIds.has(questionId) || savedMarksDataIds.has(questionId);
  }).length;

  const totalCount = requiredIds.length;
  const pendingCount = Math.max(totalCount - completedCount, 0);
  const percent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
  let status = 'pending';

  if (totalCount > 0 && completedCount === totalCount) {
    status = 'completed';
  } else if (completedCount > 0) {
    status = 'in_progress';
  }

  return {
    totalCount,
    completedCount,
    pendingCount,
    percent,
    status
  };
};

const MetricCard = ({ label, value, accent }) => {
  const accentMap = {
    indigo: 'bg-indigo-50 text-indigo-700 border-indigo-100',
    amber: 'bg-amber-50 text-amber-700 border-amber-100',
    blue: 'bg-blue-50 text-blue-700 border-blue-100',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-100'
  };

  return (
    <div className={`rounded-2xl border p-3.5 text-center shadow-sm ${accentMap[accent] || accentMap.indigo}`}>
      <p className="text-[10px] uppercase tracking-wider font-bold opacity-70">{label}</p>
      <p className="text-xl font-black mt-1 truncate">{value}</p>
    </div>
  );
};

const MiniStat = ({ label, value }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-3 text-center shadow-sm">
    <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400">{label}</p>
    <p className="text-sm font-extrabold text-slate-900 mt-1 truncate">{value}</p>
  </div>
);

const ProgressBar = ({ value }) => (
  <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
    <div
      className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-cyan-500"
      style={{ width: `${Math.min(Math.max(Number(value || 0), 0), 100)}%` }}
    />
  </div>
);

const QuestionInputRow = ({ question, value, onChange }) => {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">{question.sectionName}</p>
          <h4 className="text-sm font-extrabold text-slate-900">
            Q{question.questionNumber}{question.parentQuestionNumber ? `.${question.questionNumber}` : ''}
          </h4>
          <p className="text-xs text-slate-500 mt-1 leading-5">{question.questionText || 'No question text provided.'}</p>
        </div>
        <div className="shrink-0 text-right">
          <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Max</p>
          <p className="text-sm font-extrabold text-slate-900">{question.maxMarks}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-center">
        <input
          type="number"
          min="0"
          max={question.maxMarks}
          value={value}
          onChange={(event) => onChange(question.questionId, event.target.value)}
          className="w-full px-4 py-3 rounded-2xl border border-slate-200 bg-slate-50 text-lg font-extrabold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500"
          placeholder="0"
        />
        <div className="text-xs font-semibold text-slate-400 sm:text-right">
          {question.coMappings?.length ? `${question.coMappings.length} CO link${question.coMappings.length > 1 ? 's' : ''}` : 'No CO links'}
        </div>
      </div>
    </div>
  );
};

const EvaluatorWorkspace = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const routeState = location.state || {};
  const subjectFromState = routeState.subject || null;
  const packetFromState = routeState.packet || null;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [statusMessage, setStatusMessage] = useState({ type: '', message: '' });
  const [subjectData, setSubjectData] = useState(subjectFromState);
  const [packets, setPackets] = useState([]);
  const [summary, setSummary] = useState(null);
  const [packetData, setPacketData] = useState(null);
  const [students, setStudents] = useState([]);
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [searchText, setSearchText] = useState('');
  const [marksDraft, setMarksDraft] = useState({});
  const [savedStudentSnapshots, setSavedStudentSnapshots] = useState({});
  const [activeView, setActiveView] = useState(packetFromState ? 'packet' : 'subject');

  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || '{}');
    } catch {
      return {};
    }
  }, []);

  const showStatus = useCallback((type, message) => {
    setStatusMessage({ type, message });
    setTimeout(() => setStatusMessage({ type: '', message: '' }), 4000);
  }, []);

  const loadSubjectPackets = useCallback(async (subjectId) => {
    if (!subjectId) return;
    setLoading(true);
    try {
      const response = await api.get(`/api/evaluator/subject/${subjectId}/packets`);
      if (response.data.success) {
        setSubjectData((prev) => prev || response.data.subject || null);
        setPackets(response.data.packets || []);
        setSummary(response.data.summary || null);
      }
    } catch (error) {
      console.error('Error loading subject packets:', error);
      showStatus('error', error.response?.data?.message || 'Could not load assigned packets.');
    } finally {
      setLoading(false);
    }
  }, [showStatus]);

  const loadPacket = useCallback(async (packetId, preferredStudentId = '') => {
    if (!packetId) return;
    setLoading(true);
    try {
      const response = await api.get(`/api/evaluator/packet/${packetId}`);
      if (response.data.success) {
        const nextPacket = response.data.packet || null;
        const nextStudents = response.data.students || [];
        console.log('[EvaluatorWorkspace] packet payload', {
          packetId,
          packetNumber: nextPacket?.packetNumber,
          status: nextPacket?.status,
          totalStudents: nextPacket?.totalStudents,
          completedCount: nextPacket?.completedCount,
          pendingCount: nextPacket?.pendingCount,
          percentage: nextPacket?.percentage,
          studentCount: nextStudents.length
        });
        setPacketData(nextPacket);
        setStudents(nextStudents);
        const firstStudent = nextStudents[0] || null;
        const selectedStudent = nextStudents.find((student) => String(student.studentId) === String(preferredStudentId)) || firstStudent || null;
        setSelectedStudentId(selectedStudent?.studentId || '');
        setMarksDraft(createDraftFromStudent(nextPacket?.sections || [], selectedStudent));
        console.log('[EvaluatorWorkspace] derived progress view', {
          progress: nextPacket?.percentage || 0,
          selectedStudentId: selectedStudent?.studentId || '',
          selectedStudentName: selectedStudent ? getStudentName(selectedStudent) : ''
        });
        setSavedStudentSnapshots(Object.fromEntries(nextStudents.map((student) => [String(student.studentId), student])));
        if (response.data.packet?.subjectId) {
          setSubjectData((prev) => prev || {
            subjectId: response.data.packet.subjectId,
            subjectName: response.data.packet.subjectName,
            subjectCode: response.data.packet.subjectCode
          });
        }
      }
    } catch (error) {
      console.error('Error loading packet:', error);
      showStatus('error', error.response?.data?.message || 'Could not load packet details.');
    } finally {
      setLoading(false);
    }
  }, [showStatus]);

  useEffect(() => {
    const subjectId = subjectFromState?.subjectId;
    const packetId = packetFromState?.packetId;

    if (!subjectId && !packetId) {
      navigate('/evaluator/dashboard', { replace: true });
      return;
    }

    if (packetId) {
      setActiveView('packet');
      loadPacket(packetId);
      return;
    }

    if (subjectId) {
      setActiveView('subject');
      loadSubjectPackets(subjectId);
    }
  }, [subjectFromState, packetFromState, navigate, loadPacket, loadSubjectPackets]);

  useEffect(() => {
    if (selectedStudentId) {
      const selectedStudent = savedStudentSnapshots[String(selectedStudentId)];
      setMarksDraft(createDraftFromStudent(packetData?.sections || [], selectedStudent));
    }
  }, [selectedStudentId, savedStudentSnapshots, packetData?.sections]);

  const sortedStudents = useMemo(() => {
    return [...students].sort((a, b) => {
      const usnA = getStudentUsn(a).toLowerCase();
      const usnB = getStudentUsn(b).toLowerCase();
      if (usnA < usnB) return -1;
      if (usnA > usnB) return 1;
      return getStudentName(a).localeCompare(getStudentName(b), undefined, { sensitivity: 'base' });
    });
  }, [students]);

  const filteredStudents = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    if (!query) return sortedStudents;
    return sortedStudents.filter((student) => {
      const name = getStudentName(student).toLowerCase();
      const usn = getStudentUsn(student).toLowerCase();
      const roll = `${student.rollNumber || student.rollNo || ''}`.toLowerCase();
      return name.includes(query) || usn.includes(query) || roll.includes(query);
    });
  }, [sortedStudents, searchText]);

  const selectedStudent = useMemo(
    () => students.find((student) => String(student.studentId) === String(selectedStudentId)) || null,
    [students, selectedStudentId]
  );

  const selectedStudentMeta = useMemo(
    () => getStudentStatusMeta(selectedStudent, packetData?.sections || []),
    [selectedStudent, packetData?.sections]
  );

  const studentQueueStats = useMemo(() => {
    return students.reduce((acc, student) => {
      const meta = getStudentStatusMeta(student, packetData?.sections || []);
      acc.total += 1;
      acc.completed += meta.status === 'completed' ? 1 : 0;
      acc.pending += meta.status === 'pending' ? 1 : 0;
      acc.inProgress += meta.status === 'in_progress' ? 1 : 0;
      return acc;
    }, { total: 0, completed: 0, pending: 0, inProgress: 0 });
  }, [students, packetData?.sections]);

  const draftTotal = useMemo(() => calculateDraftTotal(marksDraft), [marksDraft]);
  const packetProgress = packetData?.percentage || 0;

  const openPacket = useCallback((packet) => {
    navigate('/evaluator/workspace', {
      state: {
        subject: subjectData,
        packet: {
          packetId: packet.packetId,
          packetNumber: packet.packetNumber,
          packetCode: packet.packetCode
        }
      }
    });
  }, [navigate, subjectData]);

  const returnToSubjectBoard = useCallback(() => {
    navigate('/evaluator/workspace', {
      state: {
        subject: subjectData
      }
    });
  }, [navigate, subjectData]);

  const handleMarksChange = useCallback((questionId, value) => {
    setMarksDraft((prev) => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        marksObtained: value === '' ? '' : Number(value)
      }
    }));
  }, []);

  const handleSaveMarks = useCallback(async () => {
    if (!packetData || !selectedStudent) return;

    setSaving(true);
    try {
      const response = await api.post('/api/evaluator/save-student-marks', {
        packetId: packetData.packetId,
        subjectId: packetData.subjectId,
        studentId: selectedStudent.studentId,
        marksObtained: draftTotal,
        marksData: marksDraft
      });

      if (response.data.success) {
        showStatus('success', `Marks saved for ${getStudentName(selectedStudent)}.`);
        await loadPacket(packetData.packetId, selectedStudent.studentId);
      }
    } catch (error) {
      console.error('Error saving marks:', error);
      showStatus('error', error.response?.data?.message || 'Failed to save marks.');
    } finally {
      setSaving(false);
    }
  }, [packetData, selectedStudent, draftTotal, marksDraft, showStatus, loadPacket]);

  const handleLogout = async () => {
    try {
      await api.post('/api/evaluator/logout');
    } catch (error) {
      console.error('Logout record failed:', error);
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('userRole');
      localStorage.removeItem('tenantId');
      navigate('/', { replace: true });
    }
  };

  const packetCards = packets || [];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm px-6 py-5 flex items-center gap-3">
          <Loader2 className="w-5 h-5 text-indigo-600 animate-spin" />
          <p className="text-sm font-medium text-slate-600">Loading evaluator workspace...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-50 to-indigo-50/40 text-slate-900">
      <header className="sticky top-0 z-20 bg-white/95 backdrop-blur border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between gap-4">
          <div className="min-w-0 flex items-center gap-3">
            <button
              onClick={() => {
                if (activeView === 'packet') {
                  returnToSubjectBoard();
                } else {
                  navigate('/evaluator/dashboard', { replace: true });
                }
              }}
              className="inline-flex items-center justify-center w-10 h-10 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 transition-colors"
              aria-label="Go back"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-lg sm:text-xl font-extrabold tracking-tight text-slate-900 truncate">
                  {activeView === 'packet' ? `Packet ${packetData?.packetNumber || ''}` : `${subjectData?.subjectName || 'Assigned Subject'} Packets`}
                </h1>
                {activeView === 'packet' && packetData?.status && (
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold border ${statusClasses[packetData.status] || 'bg-slate-50 text-slate-700 border-slate-200'}`}>
                    {packetData.status.replace('_', ' ')}
                  </span>
                )}
              </div>
              <p className="text-xs sm:text-sm text-slate-500 font-medium truncate">
                {activeView === 'packet'
                  ? `${subjectData?.subjectName || packetData?.subjectName || 'Assigned subject'} · ${subjectData?.subjectCode || packetData?.subjectCode || 'N/A'}`
                  : 'Select a packet to open the full student marking page.'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-2 text-right">
              <div>
                <p className="text-sm font-bold text-slate-800">{user?.firstName || ''} {user?.lastName || ''}</p>
                <p className="text-[11px] text-slate-400">Evaluator workspace</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-900 text-white text-xs font-bold hover:bg-slate-800 transition-colors"
            >
              <ShieldCheck className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {statusMessage.message && (
          <div className={`rounded-2xl border p-4 shadow-sm ${statusMessage.type === 'error' ? 'bg-red-50 border-red-200 text-red-800' : 'bg-emerald-50 border-emerald-200 text-emerald-800'}`}>
            <div className="flex items-start gap-3">
              {statusMessage.type === 'error' ? <AlertCircle className="w-5 h-5 mt-0.5" /> : <CheckCircle2 className="w-5 h-5 mt-0.5" />}
              <p className="text-sm font-semibold leading-6">{statusMessage.message}</p>
            </div>
          </div>
        )}

        {activeView === 'subject' ? (
          <>
            <section className="bg-white rounded-3xl border border-slate-200 shadow-sm p-5 sm:p-6 space-y-5">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="p-3 rounded-2xl bg-indigo-50 text-indigo-600 border border-indigo-100">
                    <BookOpen className="w-6 h-6" />
                  </div>
                  <div>
                    <h2 className="text-xl font-black text-slate-900">{subjectData?.subjectName || 'Assigned Subject'}</h2>
                    <p className="text-sm text-slate-500 font-medium">{subjectData?.subjectCode || 'Subject Code'} · Subject packet board</p>
                  </div>
                </div>
                <button
                  onClick={() => loadSubjectPackets(subjectData?.subjectId)}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-200 bg-white text-slate-700 text-xs font-bold hover:bg-slate-50 transition-colors"
                >
                  <RefreshCw className="w-3.5 h-3.5" /> Refresh packets
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                <MetricCard label="Total packets" value={summary?.totalPackets || packetCards.length || 0} accent="indigo" />
                <MetricCard label="Pending" value={summary?.pendingPackets || 0} accent="amber" />
                <MetricCard label="In progress" value={summary?.inProgressPackets || 0} accent="blue" />
                <MetricCard label="Completed" value={summary?.completedPackets || 0} accent="emerald" />
              </div>
            </section>

            <section className="grid grid-cols-1 xl:grid-cols-3 gap-5 items-start">
              <div className="xl:col-span-2 space-y-4">
                {packetCards.length === 0 ? (
                  <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-10 text-center">
                    <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                    <p className="text-sm font-semibold text-slate-600">No packets are assigned yet for this subject.</p>
                    <p className="text-xs text-slate-400 mt-1">Once COE assigns packets, they will appear here with progress and student counts.</p>
                  </div>
                ) : (
                  packetCards.map((packet) => (
                    <button
                      key={packet.packetId}
                      onClick={() => openPacket(packet)}
                      className="w-full text-left bg-white rounded-3xl border border-slate-200 shadow-sm p-5 hover:shadow-md hover:border-slate-300 transition-all"
                    >
                      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                        <div className="min-w-0 space-y-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="text-lg font-extrabold text-slate-900">Packet {packet.packetNumber}</h3>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold border ${statusClasses[packet.status] || 'bg-slate-50 text-slate-700 border-slate-200'}`}>
                              {packet.status.replace('_', ' ')}
                            </span>
                          </div>
                          <p className="text-xs text-slate-500 font-mono">{packet.packetCode || `PKT-${packet.packetNumber}`}</p>
                          <p className="text-sm text-slate-600 font-medium">
                            {packet.evaluationRound?.roundName || packet.evaluationRound?.roundType || 'Evaluation'} · {packet.studentCount || packet.totalStudents || 0} students
                          </p>
                          <div className="flex flex-wrap gap-3 text-[11px] text-slate-400 font-medium">
                            <span className="inline-flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {packet.totalStudents || 0} total</span>
                            <span className="inline-flex items-center gap-1"><BadgeCheck className="w-3.5 h-3.5" /> {packet.completedCount || 0} completed</span>
                            <span className="inline-flex items-center gap-1"><Clock3 className="w-3.5 h-3.5" /> {packet.pendingCount || 0} pending</span>
                          </div>
                        </div>

                        <div className="min-w-[220px] space-y-2">
                          <div className="flex items-center justify-between text-[11px] font-bold text-slate-500">
                            <span>Progress</span>
                            <span>{formatPercent(packet.percentage)}</span>
                          </div>
                          <ProgressBar value={packet.percentage || 0} />
                          <div className="flex items-center justify-between text-[11px] text-slate-400 font-medium">
                            <span>Open packet board</span>
                            <ChevronRight className="w-4 h-4 text-slate-300" />
                          </div>
                        </div>
                      </div>
                    </button>
                  ))
                )}
              </div>

              <aside className="bg-white rounded-3xl border border-slate-200 shadow-sm p-5 space-y-4 xl:sticky xl:top-24">
                <div className="flex items-center gap-2">
                  <Layers3 className="w-4 h-4 text-slate-400" />
                  <h3 className="text-sm font-extrabold text-slate-800">Subject packet summary</h3>
                </div>
                <div className="grid grid-cols-2 gap-3 text-center">
                  <MiniStat label="Packets" value={summary?.totalPackets || 0} />
                  <MiniStat label="Students" value={summary?.totalStudents || 0} />
                  <MiniStat label="Done" value={summary?.completedPackets || 0} />
                  <MiniStat label="Working" value={summary?.inProgressPackets || 0} />
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-400">How to use</p>
                  <ol className="mt-2 space-y-2 text-sm text-slate-600">
                    <li>1. Open a packet from the board.</li>
                    <li>2. Select each student from the left list.</li>
                    <li>3. Enter question-wise marks and save to update packet progress.</li>
                  </ol>
                </div>
              </aside>
            </section>
          </>
        ) : (
          <section className="grid grid-cols-1 xl:grid-cols-12 gap-6 items-start">
            <div className="xl:col-span-5 bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden xl:sticky xl:top-24">
              <div className="p-5 sm:p-6 space-y-5 bg-gradient-to-br from-slate-50 to-white border-b border-slate-200">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div className="min-w-0">
                    <p className="text-[11px] uppercase tracking-[0.28em] text-slate-400 font-bold">Packet students</p>
                    <h2 className="text-2xl font-black text-slate-900 mt-1 truncate">Packet {packetData?.packetNumber || ''}</h2>
                    <p className="text-sm text-slate-500 font-medium mt-1 truncate">
                      {packetData?.subjectName || subjectData?.subjectName || 'Assigned Subject'} · {packetData?.packetCode || 'No packet code'}
                    </p>
                  </div>
                  <button
                    onClick={() => loadPacket(packetData?.packetId, selectedStudentId)}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-2xl border border-slate-200 bg-white text-slate-700 text-xs font-extrabold hover:bg-slate-50 transition-colors"
                  >
                    <RefreshCw className="w-3.5 h-3.5" /> Refresh
                  </button>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <MiniStat label="Students" value={studentQueueStats.total || packetData?.totalStudents || students.length || 0} />
                  <MiniStat label="Done" value={studentQueueStats.completed || packetData?.completedCount || 0} />
                  <MiniStat label="Pending" value={studentQueueStats.pending || packetData?.pendingCount || 0} />
                  <MiniStat label="Visible" value={filteredStudents.length} />
                </div>

                <div className="space-y-2">
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-400">Search student</label>
                  <div className="relative">
                    <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      placeholder="Search by name, USN, or roll number"
                      className="w-full pl-9 pr-10 py-3 rounded-2xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500"
                    />
                    {searchText && (
                      <button
                        type="button"
                        onClick={() => setSearchText('')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 px-2.5 py-1 rounded-full text-[10px] font-bold text-slate-500 hover:bg-slate-100"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-slate-400 font-medium">
                    Students are automatically sorted by USN so you can move down the packet in order.
                  </p>
                </div>

                <div className="flex flex-wrap gap-2 text-[11px] font-bold">
                  <span className="px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100">Completed</span>
                  <span className="px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-100">Pending</span>
                  <span className="px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-100">In progress</span>
                </div>
              </div>

              <div className="hidden xl:grid grid-cols-[72px_minmax(0,1fr)_140px] gap-3 px-5 py-3 text-[10px] font-bold uppercase tracking-wider text-slate-400 bg-slate-50 border-b border-slate-200">
                <span>Sl No</span>
                <span>Student</span>
                <span className="text-right">Status</span>
              </div>

              <div className="max-h-[42rem] overflow-y-auto divide-y divide-slate-100">
                {filteredStudents.length === 0 ? (
                  <div className="text-center py-16 text-slate-400 px-6">
                    <Users className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                    <p className="text-sm font-semibold text-slate-600">No matching students found.</p>
                    <p className="text-xs text-slate-400 mt-1">Try a different USN or clear the search box.</p>
                  </div>
                ) : (
                  filteredStudents.map((student, index) => {
                    const isSelected = String(selectedStudentId) === String(student.studentId);
                    const studentMeta = getStudentStatusMeta(student, packetData?.sections || []);
                    const statusLabel = studentMeta.status === 'completed' ? 'Completed' : studentMeta.status === 'in_progress' ? 'In progress' : 'Pending';
                    const statusClass = studentMeta.status === 'completed'
                      ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
                      : studentMeta.status === 'in_progress'
                        ? 'bg-blue-50 text-blue-700 border-blue-100'
                        : 'bg-amber-50 text-amber-700 border-amber-100';

                    return (
                      <button
                        key={student.studentId}
                        onClick={() => {
                          setSelectedStudentId(student.studentId);
                          setMarksDraft(createDraftFromStudent(packetData?.sections || [], student));
                        }}
                        className={`w-full text-left px-5 py-4 transition-colors ${isSelected ? 'bg-indigo-50/70' : 'hover:bg-slate-50'}`}
                      >
                        <div className="grid grid-cols-[72px_minmax(0,1fr)_140px] gap-3 items-center">
                          <div className="flex items-center justify-center">
                            <div className={`h-11 w-11 rounded-2xl flex items-center justify-center font-black text-sm border ${isSelected ? 'bg-indigo-600 text-white border-indigo-500 shadow-sm' : 'bg-slate-100 text-slate-500 border-slate-200'}`}>
                              {index + 1}
                            </div>
                          </div>

                          <div className="min-w-0 space-y-1">
                            <div className="flex items-center gap-2 min-w-0">
                              <p className="text-sm font-extrabold text-slate-900 truncate">{getStudentName(student)}</p>
                              {studentMeta.status === 'completed' && <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />}
                            </div>
                            <p className="text-[11px] text-slate-500 font-mono truncate">USN: {getStudentUsn(student)}</p>
                            <div className="flex items-center gap-3 text-[11px] text-slate-400 font-semibold">
                              <span>{studentMeta.completedCount}/{studentMeta.totalCount || 0} complete</span>
                              <span>{studentMeta.percent}%</span>
                            </div>
                            <ProgressBar value={studentMeta.percent} />
                          </div>

                          <div className="flex flex-col items-end gap-2">
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold border ${statusClass}`}>
                              {statusLabel}
                            </span>
                            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-400">
                              Open <ChevronRight className="w-4 h-4" />
                            </span>
                          </div>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </div>

            <div className="xl:col-span-7 space-y-5 xl:sticky xl:top-24">
              <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-5 space-y-5">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-[11px] uppercase tracking-[0.28em] text-slate-400 font-bold">Marking workspace</p>
                    <h2 className="text-2xl font-black text-slate-900 truncate mt-1">{packetData?.subjectName || subjectData?.subjectName || 'Assigned Subject'}</h2>
                    <p className="text-sm text-slate-500 font-medium mt-1 truncate">
                      Packet {packetData?.packetNumber || ''} · {packetData?.packetCode || ''} · {packetData?.evaluationRound?.roundName || packetData?.evaluationRound?.roundType || 'Evaluation'}
                    </p>
                  </div>
                  <div className="min-w-[260px] space-y-2">
                    <div className="flex items-center justify-between text-[11px] font-bold text-slate-500">
                      <span>Packet progress</span>
                      <span>{formatPercent(packetProgress)}</span>
                    </div>
                    <ProgressBar value={packetProgress} />
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <MetricCard label="Total students" value={studentQueueStats.total || packetData?.totalStudents || students.length || 0} accent="indigo" />
                  <MetricCard label="Completed" value={studentQueueStats.completed || packetData?.completedCount || 0} accent="emerald" />
                  <MetricCard label="Pending" value={studentQueueStats.pending || packetData?.pendingCount || 0} accent="amber" />
                  <MetricCard label="Status" value={(packetData?.status || 'pending').replace('_', ' ')} accent="blue" />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 space-y-3">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-slate-400" />
                      <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Selected student</p>
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-lg font-extrabold text-slate-900 truncate">{selectedStudent ? getStudentName(selectedStudent) : 'Choose a student from the list'}</h3>
                      <p className="text-sm text-slate-500 font-medium mt-0.5">{selectedStudent ? getStudentUsn(selectedStudent) : 'Click any row on the left to begin'}</p>
                    </div>
                    {selectedStudent && (
                      <div className="flex flex-wrap items-center gap-2 text-[11px] font-bold">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full border ${selectedStudentMeta.status === 'completed' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : selectedStudentMeta.status === 'in_progress' ? 'bg-blue-50 text-blue-700 border-blue-100' : 'bg-amber-50 text-amber-700 border-amber-100'}`}>
                          {selectedStudentMeta.status === 'completed' ? 'Completed' : selectedStudentMeta.status === 'in_progress' ? 'In progress' : 'Pending'}
                        </span>
                        <span className="text-slate-400">{selectedStudentMeta.completedCount}/{selectedStudentMeta.totalCount || 0} entries saved</span>
                      </div>
                    )}
                    {selectedStudent && <ProgressBar value={selectedStudentMeta.percent} />}
                  </div>

                  <div className="rounded-2xl border border-indigo-100 bg-indigo-50/60 p-4 space-y-3">
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="w-4 h-4 text-indigo-500" />
                      <p className="text-xs font-bold uppercase tracking-wider text-indigo-500">Evaluator guide</p>
                    </div>
                    <ol className="space-y-2 text-sm text-slate-700 font-medium leading-6">
                      <li>1. Pick a student from the sorted list.</li>
                      <li>2. Enter question-wise marks in the form below.</li>
                      <li>3. Save to update completed and pending status instantly.</li>
                    </ol>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-5 space-y-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-slate-400 font-bold">Packet coverage</p>
                    <h3 className="text-base font-extrabold text-slate-900">Progress and context</h3>
                  </div>
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold border ${statusClasses[packetData?.status] || 'bg-slate-50 text-slate-700 border-slate-200'}`}>
                    {packetData?.status?.replace('_', ' ') || 'pending'}
                  </span>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <MiniStat label="Packet" value={packetData?.packetNumber || '-'} />
                  <MiniStat label="Code" value={packetData?.packetCode || '-'} />
                  <MiniStat label="Round" value={packetData?.evaluationRound?.roundName || packetData?.evaluationRound?.roundType || '-'} />
                  <MiniStat label="Progress" value={formatPercent(packetProgress)} />
                </div>

                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Users className="w-4 h-4 text-slate-400" />
                    <h4 className="text-sm font-bold text-slate-700">Student coverage</h4>
                  </div>
                  <p className="text-sm text-slate-600 font-medium leading-7">
                    {studentQueueStats.completed || 0} of {studentQueueStats.total || packetData?.totalStudents || students.length || 0} student marks have been saved. The list on the left is sorted by USN for faster checking.
                  </p>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-4">
                  <div className="flex items-center gap-2">
                    <Layers3 className="w-4 h-4 text-slate-400" />
                    <h4 className="text-sm font-extrabold text-slate-800">Question-wise marking</h4>
                  </div>

                  {packetData?.sections?.length > 0 ? (
                    <div className="space-y-4">
                      {packetData.sections.map((section) => (
                        <div key={section._id || section.sectionId || section.sectionName} className="space-y-3">
                          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-400">
                            <span>{section.sectionName}</span>
                            <span className="h-px flex-1 bg-slate-200" />
                            <span>{section.sectionMaxMarks || 0} max</span>
                          </div>

                          <div className="space-y-3">
                            {(section.questions || []).map((question) => {
                              const questionKey = String(question._id);
                              const questionDraft = marksDraft[questionKey] || {
                                marksObtained: '',
                                maxMarks: question.marks || 0,
                                questionText: question.questionText,
                                questionNumber: question.questionNumber,
                                sectionName: section.sectionName
                              };

                              return (
                                <div key={questionKey} className="space-y-3">
                                  <QuestionInputRow
                                    question={{
                                      questionId: questionKey,
                                      sectionName: section.sectionName,
                                      questionNumber: question.questionNumber,
                                      questionText: question.questionText,
                                      maxMarks: question.marks || 0,
                                      coMappings: question.coMappings || []
                                    }}
                                    value={questionDraft.marksObtained}
                                    onChange={handleMarksChange}
                                  />

                                  {(question.subQuestions || []).length > 0 && (
                                    <div className="pl-4 space-y-3 border-l-2 border-slate-100">
                                      {question.subQuestions.map((subQuestion) => {
                                        const subQuestionKey = `${question._id}:${subQuestion._id}`;
                                        const subDraft = marksDraft[subQuestionKey] || {
                                          marksObtained: '',
                                          maxMarks: subQuestion.marks || 0,
                                          questionText: subQuestion.questionText,
                                          questionNumber: subQuestion.subQuestionNumber,
                                          sectionName: section.sectionName
                                        };

                                        return (
                                          <div key={subQuestionKey} className="flex items-start gap-3 rounded-2xl border border-slate-100 bg-slate-50/80 p-3">
                                            <CornerDownRight className="w-4 h-4 text-slate-300 mt-3" />
                                            <div className="flex-1 space-y-3">
                                              <div className="flex items-start justify-between gap-3">
                                                <div className="min-w-0">
                                                  <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Sub-question</p>
                                                  <h5 className="text-sm font-bold text-slate-900">{subQuestion.subQuestionNumber}</h5>
                                                  <p className="text-xs text-slate-500 mt-1 leading-5">{subQuestion.questionText || 'No sub-question text.'}</p>
                                                </div>
                                                <div className="text-right shrink-0">
                                                  <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Max</p>
                                                  <p className="text-sm font-extrabold text-slate-900">{subQuestion.marks || 0}</p>
                                                </div>
                                              </div>
                                              <input
                                                type="number"
                                                min="0"
                                                max={subQuestion.marks || 0}
                                                value={subDraft.marksObtained}
                                                onChange={(event) => handleMarksChange(subQuestionKey, event.target.value)}
                                                className="w-full px-4 py-3 rounded-2xl border border-slate-200 bg-white text-base font-extrabold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500"
                                                placeholder="0"
                                              />
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/70 p-5 text-center text-slate-500 text-sm font-medium">
                      No question structure was found. This packet can be marked as a simple total marks entry.
                    </div>
                  )}
                </div>

                {selectedStudent && (
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={handleSaveMarks}
                      disabled={saving}
                      className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-2xl bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-extrabold text-sm shadow-sm transition-colors"
                    >
                      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      Save Marks
                    </button>
                    <button
                      onClick={returnToSubjectBoard}
                      className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-2xl border border-slate-200 bg-white text-slate-700 font-bold text-sm hover:bg-slate-50 transition-colors"
                    >
                      <ArrowLeft className="w-4 h-4" /> Back to packet board
                    </button>
                  </div>
                )}
              </div>
            </div>
          </section>
        )}
      </main>
    </div>
  );
};

export default EvaluatorWorkspace;
